/**
 * Relay v1 Slice F — Daily Prebuilt overlay for human-to-human calls.
 *
 * Mounts the full Daily UI (audio controls, mute, leave, future
 * video/screen-share) as a fullscreen iframe over the host page when
 * the visitor accepts a call invite. Uses `DailyIframe.createFrame`
 * — distinct from the voice path's `createCallObject` (headless,
 * audio-only). createFrame gives us Daily's own UI.
 *
 * Lifecycle:
 *   show()    — mount the iframe, join the room
 *   destroy() — leave + destroy the iframe (idempotent)
 *
 * On either-side hang-up, the `left-meeting` Daily event fires; we
 * call the supplied onLeave handler so the widget can POST the
 * /call/:id/end audit + tear down. The same handler runs when the
 * widget receives a `call-ended` ChatEvent (rep hung up first).
 */

import type { DailyCall, DailyEventObjectFatalError } from '@daily-co/daily-js';
import { loadDaily } from '../voice/daily-loader';

export type CallOverlayController = {
  show(): Promise<void>;
  destroy(): void;
  isActive(): boolean;
};

export type CallOverlayOptions = {
  callId: string;
  roomUrl: string;
  visitorToken: string;
  /** Optional visitor display name shown in Daily's participant list. */
  visitorName?: string;
  /** Fires when the visitor leaves OR Daily fires a fatal-error. */
  onLeave: (reason: 'visitor-left' | 'rep-left' | 'error') => void;
};

/**
 * Shared ShowPilot meeting theme for Daily Prebuilt. A calm dark call
 * surface branded to ShowPilot (ink background, brand-blue accent,
 * off-white text) — the "Zoom light brand" model: every meeting we power
 * carries a little ShowPilot. Citron stays reserved for live-data
 * signals elsewhere, so it's intentionally absent here. The rep panel
 * applies the same theme so both ends of the call match.
 */
export const SHOWPILOT_DAILY_THEME = {
  colors: {
    accent: '#0b3398',
    accentText: '#FFFFFF',
    background: '#0E1311',
    backgroundAccent: '#1A2420',
    baseText: '#F2F5F0',
    border: '#2A332E',
    mainAreaBg: '#0E1311',
    mainAreaBgAccent: '#1A2420',
    mainAreaText: '#F2F5F0',
    supportiveText: '#9AA5A0',
  },
} as const;

// Light ShowPilot brand mark shown under the call — links back to the
// marketing site for awareness. UTM-tagged so it can attribute traffic
// that originates from a live meeting. Points at www.showpilot.com
// DIRECTLY: the showpilot.ai apex is an S3 bucket redirect, and S3
// drops the query string, so going through it silently threw every one
// of these UTM tags away.
export const SHOWPILOT_MEETING_HREF =
  'https://www.showpilot.com/?utm_source=showpilot-meeting&utm_medium=live-call&utm_campaign=brand';

export function createCallOverlay(opts: CallOverlayOptions): CallOverlayController {
  let container: HTMLDivElement | null = null;
  let call: DailyCall | null = null;
  let destroyed = false;
  let active = false;

  async function show(): Promise<void> {
    if (destroyed || active) return;
    active = true;

    // Full-screen meeting surface — the video needs room to breathe; a
    // small docked card crams the tiles. Keep a branded CE-style header
    // on top so it reads as a ShowPilot Relay meeting, not raw Daily.
    container = document.createElement('div');
    container.className = 'sp-call-overlay';
    Object.assign(container.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483646',
      background: SHOWPILOT_DAILY_THEME.colors.background,
      display: 'flex',
      flexDirection: 'column',
    });

    // CE-style header — a "Live meeting" status with citron live-dot
    // (live signal) on the left, the ShowPilot Relay mark on the right.
    const header = document.createElement('div');
    Object.assign(header.style, {
      flexShrink: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      padding: '12px 14px',
      background: SHOWPILOT_DAILY_THEME.colors.background,
      borderBottom: '1px solid ' + SHOWPILOT_DAILY_THEME.colors.border,
      font: '600 13px/1 system-ui, -apple-system, sans-serif',
      color: SHOWPILOT_DAILY_THEME.colors.baseText,
    });
    header.innerHTML =
      '<span style="display:inline-flex;align-items:center;gap:7px;">' +
      '<span style="width:7px;height:7px;border-radius:50%;background:#D7FF64;' +
      'box-shadow:0 0 8px #D7FF64;"></span>Live meeting</span>' +
      '<a href="' +
      SHOWPILOT_MEETING_HREF +
      '" target="_blank" rel="noopener noreferrer" ' +
      'style="display:inline-flex;align-items:baseline;gap:5px;text-decoration:none;">' +
      '<span style="color:' +
      SHOWPILOT_DAILY_THEME.colors.baseText +
      ';font-weight:600;font-size:12px;">ShowPilot</span>' +
      '<span style="background:' +
      SHOWPILOT_DAILY_THEME.colors.backgroundAccent +
      ';color:' +
      SHOWPILOT_DAILY_THEME.colors.supportiveText +
      ';padding:1px 5px;border-radius:4px;font-size:9px;font-weight:600;' +
      'text-transform:uppercase;letter-spacing:0.16em;">Relay</span></a>';

    // Daily appends its iframe inside this wrapper; flex:1 fills the card.
    const frameWrap = document.createElement('div');
    Object.assign(frameWrap.style, {
      flex: '1 1 auto',
      minHeight: '0',
      position: 'relative',
    });

    container.appendChild(header);
    container.appendChild(frameWrap);
    document.body.appendChild(container);

    // Daily Prebuilt iframe — full UI, full controls, ShowPilot-themed.
    // The SDK is a lazy chunk (daily-loader) — show() is already async.
    const Daily = await loadDaily();
    call = Daily.createFrame(frameWrap, {
      iframeStyle: {
        width: '100%',
        height: '100%',
        border: '0',
      },
      showLeaveButton: true,
      showFullscreenButton: true,
      theme: SHOWPILOT_DAILY_THEME,
      // Visitor starts with mic on, camera off (audio-only default per
      // Slice F scope). They can opt into video from the Daily controls.
      // Rep gets the same defaults via their own token.
    });

    call.on('left-meeting', () => {
      if (destroyed) return;
      opts.onLeave('visitor-left');
    });
    call.on('error', (e: DailyEventObjectFatalError | undefined) => {
      console.warn('[relay/call-overlay] Daily error', e);
      opts.onLeave('error');
    });

    try {
      await call.join({
        url: opts.roomUrl,
        token: opts.visitorToken,
        userName: opts.visitorName,
        startVideoOff: true,
        startAudioOff: false,
      });
    } catch (err) {
      console.warn('[relay/call-overlay] join failed', err);
      opts.onLeave('error');
    }
  }

  function destroy(): void {
    if (destroyed) return;
    destroyed = true;
    active = false;
    try {
      call?.destroy();
    } catch (err) {
      console.warn('[relay/call-overlay] destroy failed', err);
    }
    call = null;
    container?.remove();
    container = null;
  }

  return { show, destroy, isActive: () => active };
}
